<i18n lang="yaml">
  ru:
    ActionEditorSuccessAlert: "Призыв к действию создан!"
    ActionEditorErrorAlert: "Что-то пошло не так"
    ActionEditorDefaultName: "Призыв к действию"
  en:
    ActionEditorSuccessAlert: "The call to action has been created!"
    ActionEditorErrorAlert: "Something went wrong"
    ActionEditorDefaultName: "Call to action"
</i18n>

<template>
  <div :class="$style.actionsEditorWrapper">
    <ActionPreview
      :preview-type="previewType"
      @update-previewType="previewType = $event"
    >
      <ActionTypeMessagePreview
        v-if="form.type === 'message'"
        :preview-type="previewType"
        :redirect="form.redirect"
        :avatar-enabled="form.avatarEnabled"
        :avatar="form.avatar"
        :title="form.title"
        :text="form.text"
        :is-dark="form.isDark"
        :branding="form.branding"
      />
      <ActionTypeChatPreview
        v-else-if="form.type === 'chat'"
        :preview-type="previewType"
        :avatar="form.avatar"
        :operator-name="form.operatorName"
        :message-number1="form.messageNumber1"
        :message-number2="form.messageNumber2"
        :quick-answer-enabled="form.quickAnswerEnabled"
        :quick-answer-value="form.quickAnswerValue"
        :branding="form.branding"
      />
      <ActionTypeModalPreview
        v-else-if="form.type === 'modal'"
        :preview-type="previewType"
        :redirect="form.redirect"
        :background-position="form.backgroundPosition"
        :background-image="form.backgroundImage"
        :logo-file="form.logoFile"
        :text="form.text"
        :extra-text-enabled="form.extraTextEnabled"
        :whatsapp-button-text="form.whatsappButtonText"
        :whatsapp-qrcode-text="form.whatsappQrcodeText"
        :telegram-button-text="form.telegramButtonText"
        :telegram-qrcode-text="form.telegramQrcodeText"
        :is-dark="form.isDark"
        :branding="form.branding"
      />
    </ActionPreview>
    <ActionSidebar
      :disabled="isDisabled"
      :name="form.name"
      :type="form.type"
      :redirect="form.redirect"
      :avatar-enabled="form.avatarEnabled"
      :avatar="form.avatar"
      :is-dark="form.isDark"
      :branding="form.branding"
      :title="form.title"
      :text="form.text"
      :background-position="form.backgroundPosition"
      :background-image="form.backgroundImage"
      :logo-file="form.logoFile"
      :extra-text-enabled="form.extraTextEnabled"
      :whatsapp-button-text="form.whatsappButtonText"
      :whatsapp-qrcode-text="form.whatsappQrcodeText"
      :telegram-button-text="form.telegramButtonText"
      :telegram-qrcode-text="form.telegramQrcodeText"
      :operator-name="form.operatorName"
      :message-number1="form.messageNumber1"
      :message-number2="form.messageNumber2"
      :quick-answer-enabled="form.quickAnswerEnabled"
      :quick-answer-value="form.quickAnswerValue"
      :query-mode="query.queryMode"
      :after-page-load-enabled="query.afterPageLoadEnabled"
      :after-page-load-seconds="query.afterPageLoadSeconds"
      :page-scroll-below-enabled="query.pageScrollBelowEnabled"
      :page-scroll-below-amount="query.pageScrollBelowAmount"
      :device-enabled="query.deviceEnabled"
      :device-type="query.deviceType"
      :location-enabled="query.locationEnabled"
      :location-matcher="query.locationMatcher"
      :location-values="query.locationValues"
      :have-all-channels="haveAllChannels"
      :is-whatsapp-disabled="isWhatsappDisabled"
      :is-subscribed="isSubscribed"
      @change-form="form[$event.key] = $event.value"
      @change-query="query[$event.key] = $event.value"
      @submit="handleSubmit"
      @handle-validation="handleValidation"
    />
  </div>
</template>

<script>
/**
 * Utils
 */
import { mapActions, mapGetters } from 'vuex'
import { uploadFiles } from '@/api/filesApi'
/**
 * UI
 */
import ActionSidebar from '@/components/Actions/ActionSidebar/ActionSidebar.vue'
import ActionPreview from '@/components/Actions/ActionPreview/ActionPreview.vue'
import ActionTypeMessagePreview from '@/components/Actions/ActionPreview/ActionTypeMessagePreview.vue'
import ActionTypeChatPreview from '@/components/Actions/ActionPreview/ActionTypeChatPreview/ActionTypeChatPreview.vue'
import ActionTypeModalPreview from '@/components/Actions/ActionPreview/ActionTypeModalPreview/ActionTypeModalPreview.vue'

const DEFAULT_VALUES = {
  message: {
    ru: {
      title: 'Кристина',
      text: 'Добрый день, меня зовут Кристина. Я ваш консультант. Что вас интересует?'
    },
    en: {
      title: 'Christina',
      text: 'Good afternoon, my name is Christina. I am your consultant. What are you interested in?'
    }
  },
  chat: {
    ru: {
      operatorName: 'Кристина',
      messageNumber1: 'Добрый день, меня зовут Кристина. Я ваш консультант.',
      messageNumber2: 'Что вас интересует?'
    },
    en: {
      operatorName: 'Christina',
      messageNumber1: 'Good afternoon, my name is Christina. I am your consultant.',
      messageNumber2: 'What are you interested in?'
    }
  },
  modal: {
    ru: {
      text: 'Оформите подписку сейчас и получите скидку 15% на Yourgood Widget. Виджет позволяет увеличить конверсию в диалоги, а пользователи получают ценные предложения. Опробуйте все преимущества в Demo-режиме.',
      extraTextEnabled: false,
      whatsappButtonText: 'Написать в WhatsApp',
      whatsappQrcodeText: 'Или с телефона:',
      telegramButtonText: 'Написать в Telegram',
      telegramQrcodeText: 'Или с телефона:'
    },
    en: {
      text: 'Subscribe now and get a 15% discount on Yourgood Widget. The widget allows you to increase the conversion to dialogs, and users receive valuable offers. Try all the advantages in Demo mode.',
      extraTextEnabled: false,
      whatsappButtonText: 'Write in WhatsApp',
      whatsappQrcodeText: 'Or with the phone:',
      telegramButtonText: 'Write in Telegram',
      telegramQrcodeText: 'Or with the phone:'
    }
  }
}

export default {
  components: {
    ActionSidebar,
    ActionPreview,
    ActionTypeMessagePreview,
    ActionTypeChatPreview,
    ActionTypeModalPreview
  },
  layout: 'cabinet',
  data () {
    return {
      isLoading: true,
      invalidForm: false,

      previewType: 'desktop',
      form: {
        // base fields
        name: this.$t('ActionEditorDefaultName'),
        type: 'message',
        redirect: this.telegramEnabled
          ? 'telegram'
          : 'whatsapp',
        avatarEnabled: true,
        avatar: null,
        isDark: false,
        branding: true,
        title: DEFAULT_VALUES.message[this.$i18n.locale].title,
        text: DEFAULT_VALUES.message[this.$i18n.locale].text,
        // only chat
        operatorName: '',
        messageNumber1: '',
        messageNumber2: '',
        quickAnswerEnabled: false,
        quickAnswerValue: [],
        // only modal
        backgroundPosition: 'left',
        backgroundImage: null,
        logoFile: null,
        extraTextEnabled: false,
        whatsappButtonText: '',
        whatsappQrcodeText: '',
        telegramButtonText: '',
        telegramQrcodeText: ''
      },
      query: {
        queryMode: 'any',
        afterPageLoadEnabled: true,
        afterPageLoadSeconds: 180,
        pageScrollBelowEnabled: true,
        pageScrollBelowAmount: 50,
        deviceEnabled: false,
        deviceType: 'desktop',
        locationEnabled: false,
        locationMatcher: 'includes',
        locationValues: []
      }
    }
  },
  computed: {
    ...mapGetters({
      currentActions: 'actionsModule/currentActions',
      isSubscribed: 'subscription/isSubscribed',
      currentSettings: 'settings/currentSettings'
    }),
    isNotFreeType () {
      return this.form.type !== 'message'
    },
    haveAllChannels () {
      return this.currentSettings.telegramEnabled && this.currentSettings.whatsappEnabled
    },
    isWhatsappDisabled () {
      return !this.currentSettings.whatsappEnabled && this.form.type === 'chat'
    },
    isDisabled () {
      if (this.invalidForm) { return true }

      if (this.isSubscribed) {
        return this.isLoading
      } else {
        if (this.isNotFreeType) { return true }
        return this.isLoading
      }
    }
  },
  watch: {
    'form.type' (val) {
      this.invalidForm = false
      if (val === 'chat' && this.currentSettings.whatsappEnabled) {
        this.form.redirect = 'whatsapp'
      }
      this.setDefaultValues()
    }
  },
  mounted () {
    const idx = this.currentActions?.length || 0
    this.form.name = `${this.$t('ActionEditorDefaultName')} ${idx + 1}`
    this.isLoading = false

    this.setDefaultValues()
  },
  methods: {
    ...mapActions({
      createAction: 'actionsModule/createAction'
    }),

    setDefaultValues () {
      const defaultValues = DEFAULT_VALUES[this.form.type][this.$i18n.locale]
      if (defaultValues) {
        Object.entries(defaultValues).forEach(([key, value]) => {
          this.form[key] = value
        })
      }
    },

    formattedQueryParams () {
      const {
        queryMode,
        afterPageLoadEnabled,
        afterPageLoadSeconds,
        pageScrollBelowEnabled,
        pageScrollBelowAmount,
        deviceEnabled,
        deviceType,
        locationEnabled,
        locationMatcher,
        locationValues
      } = this.query
      const query = []

      if (afterPageLoadEnabled && afterPageLoadSeconds >= 0) {
        query.push({
          type: 'afterPageLoad',
          afterPageLoad: { seconds: afterPageLoadSeconds }
        })
      }

      if (pageScrollBelowEnabled && pageScrollBelowAmount >= 0) {
        query.push({
          type: 'pageScrollBelow',
          pageScrollBelow: {
            unit: 'pct',
            amount: pageScrollBelowAmount
          }
        })
      }

      if (deviceEnabled) {
        query.push({
          type: 'device',
          device: {
            deviceType
          }
        })
      }

      if (locationEnabled && locationValues.length) {
        query.push({
          type: 'location',
          location: {
            matcher: locationMatcher,
            values: locationValues
          }
        })
      }

      return { [queryMode]: query }
    },

    async formattedForm () {
      switch (this.form.type) {
        case 'message': {
          const files = this.form.avatar instanceof File
            ? [this.form.avatar]
            : null

          if (files && this.form.avatarEnabled) {
            const { value: [avatar] } = await uploadFiles({ files })
            return { ...this.form, avatar }
          }
          return { ...this.form, avatar: null }
        }

        case 'chat': {
          const files = this.form.avatar instanceof File
            ? [this.form.avatar]
            : null

          if (files) {
            const { value: [avatar] } = await uploadFiles({ files })
            return { ...this.form, avatar, quickAnswer: { answer: this.form.quickAnswerValue } }
          }
          return { ...this.form, avatar: null, quickAnswer: { answer: this.form.quickAnswerValue } }
        }

        case 'modal': {
          const backgroundImage = this.form.backgroundImage instanceof File ? this.form.backgroundImage : null
          const logoFile = this.form.logoFile instanceof File ? this.form.logoFile : null
          if (backgroundImage && logoFile) {
            const { value } = await uploadFiles({ files: [backgroundImage, logoFile] })
            return { ...this.form, backgroundImage: value[0], logoFile: value[1] }
          } else if (backgroundImage) {
            const { value } = await uploadFiles({ files: [backgroundImage] })
            return { ...this.form, backgroundImage: value[0], logoFile: null }
          } else if (logoFile) {
            const { value } = await uploadFiles({ files: [logoFile] })
            return { ...this.form, backgroundImage: null, logoFile: value[0] }
          }
          return { ...this.form, backgroundImage: null, logoFile: null }
        }

        default:
          break
      }
    },

    handleValidation (haveError) {
      this.invalidForm = haveError
    },

    async handleSubmit () {
      if (this.isDisabled) { return }

      this.isLoading = true
      const form = await this.formattedForm()
      const either = await this.createAction({
        form,
        query: this.formattedQueryParams()
      })

      either.mapLeft(() => {
        this.$notify({
          title: this.$t('error'),
          text: this.$t('ActionEditorDefaultName'),
          type: 'error'
        })
        this.isLoading = false
      })

      either.mapRight(() => {
        this.$notify({
          title: this.$t('success'),
          text: this.$t('ActionEditorSuccessAlert'),
          type: 'success'
        })

        this.$router.push({ name: 'actions', query: this.$route.query })
      })
    }
  }
}
</script>

<style module lang="scss">
.actionsEditorWrapper {
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
}
</style>
